import * as Sentry from '@sentry/react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';
import { useEffect } from 'react';

/**
 * Initialize Sentry in the application
 * @param dsn The Sentry DSN (Data Source Name) token
 */
export const initSentry = (dsn: string): void => {
  if (!dsn) {
    console.warn('Sentry DSN not provided. Error tracking disabled.');
    return;
  }

  Sentry.init({
    dsn: dsn,
    integrations: [
      // Use reactRouterV6BrowserTracingIntegration for React Router v6
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
    ],
    
    // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
    // We recommend adjusting this value in production to avoid performance impact.
    tracesSampleRate: 0.5,
    
    // Set tracePropagationTargets to control for which URLs trace propagation should be enabled
    tracePropagationTargets: [/^\//, /^https:\/\/app.mesadherents.fr\/api/],
    
    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: 0.1,
    
    // If the entire session is not sampled, use the below sample rate to sample
    // sessions when an error occurs.
    replaysOnErrorSampleRate: 1.0,
    
    // Enable debug in development only
    debug: import.meta.env.MODE === 'development',
    
    // Only enable in production
    enabled: import.meta.env.MODE === 'production',
    
    // Set environment
    environment: import.meta.env.MODE,
    
    // We recommend adjusting this value in production
    maxBreadcrumbs: 50,

    // Customize the beforeSend callback to filter sensitive data
    beforeSend(event) {
      // You can modify the event here to remove PII or sensitive data
      if (event.user) {
        // Keep user ID but remove other identifiable information
        delete event.user.ip_address;
        delete event.user.email;
      }
      return event;
    },
  });
};

/**
 * Set user information in Sentry for better error tracking
 * @param id User ID
 * @param role User role (optional)
 */
export const setSentryUser = (id: string, role?: string): void => {
  Sentry.setUser({
    id: id,
    ...(role && { role: role })
  });
};

/**
 * Clear user information from Sentry (call on logout)
 */
export const clearSentryUser = (): void => {
  Sentry.setUser(null);
};

/**
 * Capture an error message in Sentry
 * @param message Error message
 * @param level Error level
 */
export const captureMessage = (message: string, level: 'info' | 'warning' | 'error' | 'fatal' = 'error'): void => {
  Sentry.captureMessage(message, level);
};

/**
 * Capture an exception in Sentry
 * @param error Error object
 */
export const captureException = (error: Error): void => {
  Sentry.captureException(error);
}; 